import {
  create_requestor_application_note,
  fetch_requestor_application_journals,
  fetch_requestor_entity_profiles,
} from '@/services/requestor_portal_service';
import {
  DEFAULT_AMOUNT,
  DEFAULT_RATE,
  format_rate,
  format_hours,
  initials,
  journal_activity,
  journal_amount,
  journal_date,
  journal_hours,
  journal_is_billable,
  journal_rate_value,
  journal_role,
  journal_user_name,
  month_key,
  month_label,
  normalize_text,
  number_value,
  period_label,
  safe_date,
  submitted_meta,
  sum_hours,
  week_label,
  week_start,
} from './time_sheets_formatters';

const FILTER_DEFINITIONS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending review' },
  { key: 'approved', label: 'Ready for billing' },
  { key: 'billed', label: 'Billed' },
  { key: 'disputed', label: 'Disputed' },
];
const AVATAR_COLORS = ['#0c5144', '#58318f', '#a77729', '#315e78', '#27589f'];
const CASE_CODE_FIELDS = ['case_code', 'application_code', 'reference'];
const CASE_TITLE_FIELDS = ['title', 'entity_profile_name', 'name', 'description'];
const CASE_DOMAIN_FIELDS = ['domain', 'domain_name', 'entity_type_name', 'entity_type_label'];
const REVIEW_NOTE_PREFIX = 'Timesheet Review';
const REVIEW_STATUS_CONFIG = {
  pending: {
    status: 'Pending review',
    status_key: 'pending',
    status_badge_class: 'bg-warning-subtle text-warning-emphasis border border-warning-subtle',
    action_label: 'Review',
    action_variant: 'primary',
  },
  approved: {
    status: 'Ready for billing',
    status_key: 'approved',
    status_badge_class: 'bg-success-subtle text-success-emphasis border border-success-subtle',
    action_label: 'Invoice',
    action_variant: 'primary',
  },
  disputed: {
    status: 'Returned for revision',
    status_key: 'disputed',
    status_badge_class: 'bg-danger-subtle text-danger-emphasis border border-danger-subtle',
    action_label: 'View revision',
    action_variant: 'secondary',
  },
};

export async function load_timesheet_sheets(params = {}) {
  const profiles = await fetch_timesheet_profiles(params);
  const results = await Promise.allSettled(profiles.map(profile_timesheet_entries));
  const entries = results
    .filter((result) => result.status === 'fulfilled')
    .flatMap((result) => result.value);

  return group_timesheet_entries(entries)
    .filter((group) => group.some((entry) => entry.hours_value > 0))
    .map(map_timesheet_group);
}

export async function persist_review_status(sheet = {}, status = '', comment = '', reviewer_user_id = null) {
  const application_id = first_review_application_id(sheet);
  if (!application_id || !review_status_config(status)) {
    throw new Error('This timesheet is missing the reference needed to save the review decision.');
  }

  await create_requestor_application_note({
    application_id,
    user_id: reviewer_user_id,
    journal_description: review_note_description(sheet, status, comment),
    hours_spent: '0.00',
    journal_date: sheet.review_week_start,
  });
}

export function build_timesheet_filters(sheets = []) {
  return FILTER_DEFINITIONS.map((filter) => ({
    ...filter,
    count: count_for_filter(sheets, filter.key),
  }));
}

export function build_timesheet_metrics(sheets = []) {
  const pending = count_for_filter(sheets, 'pending');
  const approved = count_for_filter(sheets, 'approved');
  const billed = count_for_filter(sheets, 'billed');
  const disputed = count_for_filter(sheets, 'disputed');

  return [
    metric('Pending Review', pending, 'Submitted, awaiting your approval', 'secondary-color', 'bi-hourglass-split'),
    metric('Ready for Billing', approved, `${approved} timesheets ready to bill`, 'text-purple', 'bi-check2-square'),
    metric('Billed This Month', billed, `${billed} invoiced`, 'secondary-color', 'bi-receipt'),
    metric('Disputed', disputed, 'Returned for handler revision', 'text-purple', 'bi-exclamation-circle'),
  ];
}

export function timesheet_periods(sheets = []) {
  return [...new Set(sheets.map((sheet) => sheet.period).filter(Boolean))];
}

export function timesheet_months(sheets = []) {
  const month_map = sheets.reduce((months, sheet) => {
    if (sheet.month_key && !months.has(sheet.month_key)) {
      months.set(sheet.month_key, sheet.month_label || sheet.month_key);
    }
    return months;
  }, new Map());

  return Array.from(month_map.entries())
    .sort(([left], [right]) => right.localeCompare(left))
    .map(([value, label]) => ({ value, label }));
}

export function timesheet_export_csv(sheets = []) {
  const headers = [
    'Handler',
    'Role',
    'Date',
    'Cases',
    'Billable',
    'Non-Billable',
    'Split',
    'Amount',
    'Rate',
    'Status',
  ];
  const rows = sheets.map((sheet) => [
    sheet.handler,
    sheet.role,
    sheet.submitted_date,
    sheet.cases,
    sheet.billable,
    sheet.non_bill,
    sheet.split_label,
    export_amount(sheet.amount),
    sheet.rate,
    sheet.status,
  ]);

  return `\uFEFFsep=,\r\n${[headers, ...rows].map((row) => row.map(csv_value).join(',')).join('\r\n')}`;
}

export function timesheet_export_filename(month_key_value = '') {
  const stamp = month_key_value || new Date().toISOString().slice(0, 7);
  return `timesheets-billing-${stamp}.csv`;
}

export function review_entries(sheet = {}) {
  if (sheet?.review_entries?.length) {
    return sheet.review_entries;
  }

  return [weekly_total_entry(sheet), internal_time_entry(sheet)];
}

export function review_billable_report_sections(sheet = {}) {
  const entries = review_entries(sheet);
  const summary = billable_review_summary(entries);

  return [
    billable_metrics_section(summary),
    billable_timekeeper_section(entries, summary),
    billable_case_section(entries, summary),
    billable_detailed_entries_section(entries),
  ];
}

function weekly_total_entry(sheet = {}) {
  return {
    code: 'WEEKLY TOTAL',
    matter_id: 'WEEKLY TOTAL',
    client_case_name: `${sheet?.cases || 0} cases submitted for review`,
    practice_area: 'Timesheet review',
    date: sheet?.week || '',
    timekeeper: sheet?.handler || 'Unknown handler',
    title: sheet?.role || 'Not returned',
    hourly_rate: sheet?.rate || DEFAULT_RATE,
    activity: `${sheet?.cases || 0} cases submitted for review`,
    task: `${sheet?.cases || 0} cases submitted for review`,
    hours: sheet?.billable || '0h',
    billable_hours: hours_number(sheet?.billable),
    non_billable_hours: 0,
    billable: true,
    amount: sheet?.amount || DEFAULT_AMOUNT,
    total_value: sheet?.amount || DEFAULT_AMOUNT,
  };
}

function internal_time_entry(sheet = {}) {
  return {
    code: 'INTERNAL',
    matter_id: 'INTERNAL',
    client_case_name: 'Non-billable time',
    practice_area: 'Internal',
    date: sheet?.week || '',
    timekeeper: sheet?.handler || 'Unknown handler',
    title: sheet?.role || 'Not returned',
    hourly_rate: sheet?.rate || DEFAULT_RATE,
    activity: 'Non-billable time',
    task: 'Non-billable time',
    hours: sheet?.non_bill || '0h',
    billable_hours: 0,
    non_billable_hours: hours_number(sheet?.non_bill),
    billable: false,
    amount: DEFAULT_AMOUNT,
    total_value: DEFAULT_AMOUNT,
  };
}

export function apply_review_status(sheet = {}, status = '') {
  const config = review_status_config(status) || REVIEW_STATUS_CONFIG.pending;
  const submitted_meta = status === 'approved'
    ? 'Marked ready for billing'
    : status === 'disputed'
      ? 'Returned just now - revision requested'
      : 'Submitted for review';

  return Object.assign(sheet, {
    ...config,
    submitted_meta,
  });
}

export function filtered_timesheet_sheets(sheets = [], active_filter = 'all') {
  return active_filter === 'all'
    ? sheets
    : sheets.filter((sheet) => sheet.status_key === active_filter);
}

async function fetch_timesheet_profiles(params = {}) {
  const response = await fetch_requestor_entity_profiles({
    page: 1,
    page_size: 500,
    ...params,
  });
  return response.data || [];
}

async function profile_timesheet_entries(profile = {}) {
  const application_id = application_id_from_profile(profile);
  if (!application_id) return [];

  const journals = await fetch_requestor_application_journals(application_id);
  return journals
    .map((journal, index) => map_journal_entry(journal, profile, index))
    .filter((entry) => entry.hours_value > 0 || entry.review_event);
}

function map_journal_entry(journal = {}, profile = {}, index = 0) {
  const billable = journal_is_billable(journal);
  const hours_value = journal_hours(journal);
  const review_event = review_event_from_journal(journal);
  const evidence_files = journal_documents(journal);

  return {
    id: journal.id || `${application_id_from_profile(profile)}-${index}`,
    application_id: application_id_from_profile(profile),
    handler: review_event?.handler || journal_user_name(journal),
    role: review_event?.role || journal_role(journal),
    case_code: case_code(profile),
    case_title: case_title(profile),
    case_domain: case_domain(profile),
    activity: journal_activity(journal),
    billable,
    hours_value,
    amount: journal_amount(journal, billable),
    journal_date: journal_date(journal),
    hourly_rate: journal_rate_value(journal),
    week_start: review_event?.week_start || week_start(journal_date(journal)),
    submitted_at: journal.date_created || journal_date(journal),
    review_event,
    evidence_files,
  };
}

function group_timesheet_entries(entries = []) {
  const grouped = entries.reduce((groups, entry) => {
    const key = `${entry.handler}|${entry.week_start}`;
    const next_group = groups.get(key) || [];
    next_group.push(entry);
    groups.set(key, next_group);
    return groups;
  }, new Map());

  return Array.from(grouped.values());
}

function map_timesheet_group(entries = [], group_index = 0) {
  const first_entry = entries[0] || {};
  const review_state = group_review_state(entries);
  const time_entries = entries.filter((entry) => entry.hours_value > 0);
  const billable_hours = sum_hours(time_entries.filter((entry) => entry.billable));
  const non_bill_hours = sum_hours(time_entries.filter((entry) => !entry.billable));
  const total_hours = billable_hours + non_bill_hours;
  const split = total_hours ? Math.round((billable_hours / total_hours) * 100) : 0;
  const status_config = review_status_config(review_state.status_key) || REVIEW_STATUS_CONFIG.pending;

  return {
    id: `ts-${first_entry.handler}-${first_entry.week_start}`,
    handler: first_entry.handler || 'Unknown handler',
    initials: initials(first_entry.handler),
    avatar_color: AVATAR_COLORS[group_index % AVATAR_COLORS.length],
    role: first_entry.role,
    week: week_label(first_entry.week_start),
    period: period_label(first_entry.week_start),
    month_key: month_key(first_entry.week_start),
    month_label: month_label(first_entry.week_start),
    submitted_date: latest_submission_date(time_entries),
    submitted_meta: review_state.submitted_meta,
    cases: String(new Set(time_entries.map((entry) => entry.case_code)).size),
    billable: format_hours(billable_hours),
    non_bill: format_hours(non_bill_hours),
    split_pct: `${split}%`,
    split_color: split >= 80 ? '#1f6b4f' : '#bf8430',
    split_label: split >= 80 ? `${split}% billable` : `${split}% - below 80% target`,
    split_text_class: split >= 80 ? 'secondary-color' : 'text-warning',
    amount: group_amount(time_entries),
    rate: group_rate(time_entries),
    ...status_config,
    reference: reference_label(first_entry),
    total_logged: format_hours(total_hours),
    review_entries: time_entries.map(review_entry),
    review_comment: review_state.comment,
    review_week_start: first_entry.week_start,
    review_application_ids: unique_application_ids(time_entries),
  };
}

function review_entry(entry = {}) {
  return {
    code: entry.case_code || 'INTERNAL',
    matter_id: entry.case_code || 'INTERNAL',
    application_id: entry.application_id || null,
    client_case_name: entry.case_title || 'Untitled case',
    practice_area: entry.case_domain || 'Not returned',
    date: entry.journal_date || '',
    timekeeper: entry.handler || 'Unknown timekeeper',
    title: entry.role || 'Not returned',
    hourly_rate: entry.hourly_rate || DEFAULT_RATE,
    activity: entry.activity || 'Time journal entry',
    task: entry.activity || 'Time journal entry',
    hours: format_hours(entry.hours_value),
    billable_hours: entry.billable ? entry.hours_value : 0,
    non_billable_hours: entry.billable ? 0 : entry.hours_value,
    billable: entry.billable,
    amount: entry.amount,
    total_value: entry.amount,
    evidence_files: entry.evidence_files || [],
  };
}

function journal_documents(journal = {}) {
  const documents = Array.isArray(journal?.documents) ? journal.documents : [];
  return documents
    .map((document, index) => ({
      id: document?.id || `${journal?.id || 'journal'}-doc-${index}`,
      name: normalize_text(document?.document_name || `Evidence ${index + 1}`),
      url: normalize_text(document?.signed_file_url || document?.file_url || ''),
      uploaded_by: normalize_text(document?.uploaded_by),
      date_uploaded: normalize_text(document?.date_uploaded),
    }))
    .filter((document) => document.name || document.url);
}

function billable_metrics_section(summary = {}) {
  return {
    title: '1. Executive Summary',
    type: 'metrics',
    rows: [
      { metric: 'Total Active Cases', value: summary.active_cases },
      { metric: 'Total Timekeepers', value: summary.timekeepers },
      { metric: 'Total Billable Hours', value: summary.billable_hours },
      { metric: 'Total Non-Billable Hours', value: summary.non_billable_hours },
      { metric: 'Total Billed Value', value: summary.total_value },
      { metric: 'Overall Team Utilization Rate', value: `${summary.utilization_rate}%` },
    ],
  };
}

function billable_timekeeper_section(entries = [], summary = {}) {
  return {
    title: '2. Summary by Timekeeper',
    type: 'table',
    columns: [
      { key: 'timekeeper', label: 'Timekeeper' },
      { key: 'title', label: 'Title' },
      { key: 'hourly_rate', label: 'Hourly Rate' },
      { key: 'billable_hours', label: 'Billable Hours' },
      { key: 'total_value', label: 'Total Value' },
    ],
    rows: [...timekeeper_rows(entries), {
      timekeeper: 'TOTAL',
      title: '',
      hourly_rate: '',
      billable_hours: summary.billable_hours,
      total_value: summary.total_value,
    }],
  };
}

function billable_case_section(entries = [], summary = {}) {
  return {
    title: '3. Summary by Case / Matter',
    type: 'table',
    columns: [
      { key: 'matter_id', label: 'Matter ID' },
      { key: 'client_case_name', label: 'Client / Case Name' },
      { key: 'practice_area', label: 'Practice Area' },
      { key: 'total_hours', label: 'Total Hours' },
      { key: 'total_value', label: 'Total Billed Value' },
    ],
    rows: [...case_rows(entries), {
      matter_id: 'TOTAL',
      client_case_name: '',
      practice_area: '',
      total_hours: summary.billable_hours,
      total_value: summary.total_value,
    }],
  };
}

function billable_detailed_entries_section(entries = []) {
  return {
    title: '4. Detailed Time Entries',
    type: 'grouped_entries',
    groups: grouped_rows(entries, (entry) => entry.matter_id).map((group) => ({
      title: `Matter: ${group.key || 'Unassigned'} | ${group.rows[0]?.client_case_name || 'Untitled case'}`,
      rows: group.rows,
    })),
  };
}

function billable_review_summary(entries = []) {
  const billable_hours = sum_field(entries, 'billable_hours');
  const non_billable_hours = sum_field(entries, 'non_billable_hours');
  const total_hours = billable_hours + non_billable_hours;

  return {
    active_cases: new Set(entries.map((entry) => entry.matter_id).filter(Boolean)).size,
    timekeepers: new Set(entries.map((entry) => entry.timekeeper).filter(Boolean)).size,
    billable_hours,
    non_billable_hours,
    total_value: first_value(entries.map((entry) => entry.total_value)),
    utilization_rate: total_hours ? Math.round((billable_hours / total_hours) * 100) : 0,
  };
}

function timekeeper_rows(entries = []) {
  return grouped_rows(entries, (entry) => entry.timekeeper).map((group) => ({
    timekeeper: group.key,
    title: first_value(group.rows.map((entry) => entry.title)),
    hourly_rate: first_value(group.rows.map((entry) => entry.hourly_rate)),
    billable_hours: sum_field(group.rows, 'billable_hours'),
    total_value: first_value(group.rows.map((entry) => entry.total_value)),
  }));
}

function case_rows(entries = []) {
  return grouped_rows(entries, (entry) => entry.matter_id).map((group) => ({
    matter_id: group.key,
    client_case_name: first_value(group.rows.map((entry) => entry.client_case_name)),
    practice_area: first_value(group.rows.map((entry) => entry.practice_area)),
    total_hours: sum_field(group.rows, 'billable_hours'),
    total_value: first_value(group.rows.map((entry) => entry.total_value)),
  }));
}

function grouped_rows(rows = [], key_fn) {
  const groups = rows.reduce((result, row) => {
    const key = key_fn(row) || 'Unassigned';
    result.set(key, [...(result.get(key) || []), row]);
    return result;
  }, new Map());

  return Array.from(groups.entries()).map(([key, rows]) => ({ key, rows }));
}

function sum_field(rows = [], field = '') {
  return Number(rows.reduce((sum, row) => sum + Number(row?.[field] || 0), 0).toFixed(2));
}

function first_value(values = []) {
  return values.find((value) => value !== undefined && value !== null && value !== '') || '-';
}

function hours_number(value = '') {
  const parsed = Number(String(value || '').replace(/[^\d.]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

function count_for_filter(sheets = [], key = 'all') {
  return key === 'all' ? sheets.length : sheets.filter((sheet) => sheet.status_key === key).length;
}

function metric(label, value, change, change_class, icon) {
  return { label, value: String(value), change, change_class, icon };
}

function csv_value(value = '') {
  const text = export_text(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function export_amount(value = '') {
  const text = String(value || '').trim();
  return text === DEFAULT_AMOUNT || text === '\u00e2\u20ac\u201d' || text === '\u2014' ? '-' : text;
}

function application_id_from_profile(profile = {}) {
  return number_value(first_profile_value([
    profile.application_id,
    profile.latest_application_id,
    profile.current_application_id,
  ]));
}

function case_code(profile = {}) {
  return normalize_text(first_profile_value([...profile_values(profile, CASE_CODE_FIELDS), fallback_case_code(profile)]));
}

function case_title(profile = {}) {
  return normalize_text(first_profile_value(profile_values(profile, CASE_TITLE_FIELDS))) || 'Untitled case';
}

function case_domain(profile = {}) {
  return normalize_text(first_profile_value(profile_values(profile, CASE_DOMAIN_FIELDS))) || 'Not returned';
}

function first_profile_value(values = []) {
  return values.find(Boolean);
}

function profile_values(profile = {}, fields = []) {
  return fields.map((field) => profile_field(profile, field));
}

function profile_field(profile = {}, field = '') {
  const entry = Object.entries(profile).find(([key]) => key === field);
  return entry ? entry[1] : '';
}

function fallback_case_code(profile = {}) {
  return `APP-${application_id_from_profile(profile) || 'N/A'}`;
}

function reference_label(entry = {}) {
  const compact_handler = normalize_text(entry.handler).replace(/\s+/g, '-').toUpperCase();
  return `TS-${entry.week_start}-${compact_handler}`;
}

function group_amount(entries = []) {
  const total = entries
    .map((entry) => amount_number(entry.amount))
    .reduce((sum, amount) => sum + amount, 0);
  return total > 0 ? format_money(total) : DEFAULT_AMOUNT;
}

function group_rate(entries = []) {
  const rate = entries.map((entry) => Number(entry.hourly_rate || 0)).find((value) => value > 0);
  return rate ? format_rate(rate) : DEFAULT_RATE;
}

function amount_number(value = '') {
  const normalized = String(value || '')
    .replace(/\u00a0/g, ' ')
    .replace(/[^\d,.]/g, '')
    .replace(/\s+/g, '');
  const decimal_normalized = normalized.includes(',') && !normalized.includes('.')
    ? normalized.replace(',', '.')
    : normalized.replace(/,/g, '');
  const parsed = Number(decimal_normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function format_money(value = 0) {
  const [whole, cents] = Number(value || 0).toFixed(2).split('.');
  return `R${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${cents}`;
}

function latest_submission_date(entries = []) {
  const latest = entries
    .map((entry) => safe_date(entry.submitted_at).getTime())
    .sort((left, right) => right - left)[0];
  return latest ? new Date(latest).toLocaleDateString('en-ZA') : '';
}

function export_text(value = '') {
  return String(value ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/\u00c2/g, '');
}

function unique_application_ids(entries = []) {
  return [...new Set(entries.map((entry) => Number(entry.application_id)).filter((value) => Number.isFinite(value) && value > 0))];
}

function first_review_application_id(sheet = {}) {
  return Number(sheet.review_application_ids?.[0]) || Number(sheet.review_entries?.[0]?.application_id) || null;
}

function review_note_description(sheet = {}, status = '', comment = '') {
  const parts = [
    REVIEW_NOTE_PREFIX,
    `Week: ${sheet.review_week_start || ''}`,
    `Handler: ${sheet.handler || ''}`,
    `Role: ${sheet.role || ''}`,
    `Status: ${status}`,
  ];
  const trimmed_comment = normalize_text(comment);
  if (trimmed_comment) {
    parts.push(`Comment: ${trimmed_comment}`);
  }
  return parts.join(' | ');
}

function review_event_from_journal(journal = {}) {
  const description = normalize_text(journal.journal_description);
  if (!description.startsWith(REVIEW_NOTE_PREFIX)) return null;

  const metadata = description
    .split('|')
    .map((part) => part.trim())
    .slice(1)
    .reduce((result, part) => {
      const [raw_key, ...value_parts] = part.split(':');
      const key = normalize_text(raw_key).toLowerCase().replace(/\s+/g, '_');
      const value = normalize_text(value_parts.join(':'));
      if (key) result[key] = value;
      return result;
    }, {});
  const status_key = normalize_review_status_key(metadata.status);
  const week_value = metadata.week;

  if (!status_key || !week_value) return null;

  return {
    status_key,
    week_start: week_start(week_value),
    handler: metadata.handler || '',
    role: metadata.role || '',
    comment: metadata.comment || '',
    created_at: journal.date_created || journal.created_at || journal.journal_date,
  };
}

function normalize_review_status_key(value = '') {
  const normalized = normalize_text(value).toLowerCase();
  if (normalized === 'approved') return 'approved';
  if (['disputed', 'returned', 'returned for revision', 'revision', 'returned-for-revision'].includes(normalized)) {
    return 'disputed';
  }
  return '';
}

function group_review_state(entries = []) {
  const review_events = entries
    .map((entry) => entry.review_event)
    .filter(Boolean)
    .sort((left, right) => safe_timestamp(right.created_at) - safe_timestamp(left.created_at));
  const latest_review = review_events[0] || null;
  const latest_submission_at = entries
    .filter((entry) => entry.hours_value > 0)
    .map((entry) => safe_timestamp(entry.submitted_at))
    .sort((left, right) => right - left)[0] || 0;
  const latest_review_at = latest_review ? safe_timestamp(latest_review.created_at) : 0;

  if (!latest_review || latest_submission_at > latest_review_at) {
    return {
      status_key: 'pending',
      submitted_meta: submitted_meta(entries),
      comment: '',
    };
  }

  if (latest_review.status_key === 'approved') {
    return {
      status_key: 'approved',
      submitted_meta: 'Ready for billing',
      comment: latest_review.comment || '',
    };
  }

  return {
    status_key: 'disputed',
    submitted_meta: 'Returned for revision',
    comment: latest_review.comment || '',
  };
}

function review_status_config(status = '') {
  return REVIEW_STATUS_CONFIG[status] || null;
}

function safe_timestamp(value = '') {
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}
